import { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  Eye,
  EyeOff,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  FileDown,
} from "lucide-react";
import { cn } from "@/lib/utils.js";
import {
  dateRangePillIdle,
  dateRangePillSelected,
  dateRangePillTrack,
  steelChromeButton,
} from "@/lib/status-styles.js";
import CustomDateRangePicker from "./custom-date-range-picker";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

import {
  ANNOTATION_OFFSET_PX,
  DOWNTIME_COLORS,
  DOWNTIME_HTML_LABEL_SLOT_PX,
  MOUSE_THROTTLE_MS,
  MAX_LABEL_LENGTH,
  SERIES_COLORS,
  TREND_X_AXIS_MARGIN_MULTI,
  TREND_X_AXIS_MARGIN_SINGLE,
} from "@/constants/chart-constants.js";
import { downloadTrendAnalysis } from "@/lib/csv-utils.js";
import { sameDowntimeAssetPk } from "@/lib/downtime-data-utils.js";
import { getChartTooltipChrome, formatChartTooltipDateTime } from "@/utils/chart-utils.js";
import { useTheme } from "@/contexts/ThemeContext.jsx";

// Base Highcharts axis configuration
const baseAxisConfig = {
  labels: {
    style: {
      fontSize: "12px",
      color: "hsl(var(--muted-foreground))",
    },
  },
  gridLineColor: "hsl(var(--border))",
};

/**
 * Trend Analysis Panel Component
 * Displays chart with zoom controls and date range selection
 * @param {string|number|null|undefined} assetId - Fleet `asset.id` (same numeric PK as each downtime summary row's `assetId`)
 */
export default function TrendAnalysisPanel({
  selectedParameters,
  selectedTimeRange,
  setSelectedTimeRange,
  previousTimeRange,
  setPreviousTimeRange,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  applyCustomDateRange,
  timeRangeOptions,
  trendData,
  isLoadingTrend,
  trendError,
  isZoomed,
  setIsZoomed,
  fetchTrendData,
  createChartSeries,
  formatMultiSeriesTooltip,
  downtimeEvents,
  assetId,
}) {
  const { theme } = useTheme();
  // Ref to access the Highcharts chart instance
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const annotationContainerRef = useRef(null);
  /** Flex slot for the plot; reflow chart when this box resizes (e.g. vertical window resize). */
  const chartAreaRef = useRef(null);

  // Cache for trend data by time range and parameters
  // Structure: Map<timeRange-paramIds, { data, timestamp }>
  const trendDataCacheRef = useRef(new Map());

  // Local state for cached trend data (for instant switching)
  const [cachedTrendData, setCachedTrendData] = useState(null);

  // State to track which annotation is being hovered
  const [hoveredAnnotationIndex, setHoveredAnnotationIndex] = useState(null);

  // State to track whether downtime overlay is visible
  const [showDowntimeOverlay, setShowDowntimeOverlay] = useState(true);

  // Refs for performance optimization
  const mouseMoveThrottleRef = useRef(null);
  const lastHoveredIndexRef = useRef(null);
  const rafIdRef = useRef(null);
  const needsXAxisResetRef = useRef(false);
  const previousTimeRangeRef = useRef(selectedTimeRange);

  // Helper: Get chart instance with fallback strategies
  const getChartInstance = useCallback(() => {
    let chart = chartInstanceRef.current;

    if (!chart && chartRef.current) {
      if (chartRef.current.chart) {
        chart = chartRef.current.chart;
      } else if (typeof chartRef.current.getChart === "function") {
        chart = chartRef.current.getChart();
      }
    }

    if (!chart && Highcharts.charts) {
      const validCharts = Highcharts.charts.filter(
        (c) => c && c.xAxis && c.xAxis[0]
      );
      if (validCharts.length > 0) {
        chart = validCharts[validCharts.length - 1];
      }
    }

    return chart?.xAxis?.[0] ? chart : null;
  }, []);

  // Handlers for zoom buttons
  const handleZoomIn = useCallback(() => {
    const chart = getChartInstance();
    if (!chart) return;

    const xAxis = chart.xAxis[0];
    const extremes = xAxis.getExtremes();

    if (!extremes?.min || !extremes?.max) return;

    const range = extremes.max - extremes.min;
    if (range <= 0) return;

    // Zoom into the rightmost 50% of the current view
    const newRange = range * 0.5;
    const newMin = extremes.max - newRange;
    const newMax = extremes.max;

    // Ensure we don't zoom beyond data bounds
    const dataMin = xAxis.dataMin;
    const dataMax = xAxis.dataMax;
    const finalMin = Math.max(dataMin ?? newMin, newMin);
    const finalMax = Math.min(dataMax ?? newMax, newMax);

    if (finalMax > finalMin) {
      xAxis.setExtremes(finalMin, finalMax, true);
      setIsZoomed(true);
    }
  }, [getChartInstance, setIsZoomed]);

  const handleZoomOut = useCallback(() => {
    const chart = getChartInstance();
    if (!chart) return;

    const xAxis = chart.xAxis[0];
    const extremes = xAxis.getExtremes();

    if (!extremes?.min || !extremes?.max) return;

    const range = extremes.max - extremes.min;
    if (range <= 0) return;

    // Get data bounds
    const dataMin = xAxis.dataMin;
    const dataMax = xAxis.dataMax;

    // Zoom out by 2x (double the current range)
    const newRange = range * 2;
    const center = extremes.min + range / 2;
    const newMin = center - newRange / 2;
    const newMax = center + newRange / 2;

    // Ensure we don't zoom beyond data bounds
    const finalMin = Math.max(dataMin ?? newMin, newMin);
    const finalMax = Math.min(dataMax ?? newMax, newMax);

    // If we're already at full data range, don't zoom out further
    if (finalMin <= dataMin && finalMax >= dataMax) {
      // Already at full view, reset completely
      xAxis.setExtremes(null, null, true);
      setIsZoomed(false);
    } else {
      // Zoom out incrementally
      xAxis.setExtremes(finalMin, finalMax, true);
      // Check if we're still zoomed after zooming out
      const newExtremes = xAxis.getExtremes();
      const isStillZoomed =
        newExtremes.min !== dataMin || newExtremes.max !== dataMax;
      setIsZoomed(isStillZoomed);
    }
  }, [getChartInstance, setIsZoomed]);

  // Create cache key from time range and selected parameters
  const getCacheKey = useCallback(() => {
    if (!selectedParameters || selectedParameters.size === 0) return null;
    // Create a stable key from time range and parameter IDs
    // selectedParameters is a Set of parameter ID strings
    const paramIds = Array.from(selectedParameters).sort().join(",");
    return `${selectedTimeRange}-${paramIds}`;
  }, [selectedTimeRange, selectedParameters]);

  // Cache trend data when it's received (only for non-Custom ranges)
  useEffect(() => {
    if (
      trendData
      && trendData.length > 0
      && selectedTimeRange !== "Custom"
      && !isLoadingTrend
    ) {
      const cacheKey = getCacheKey();
      if (cacheKey) {
        trendDataCacheRef.current.set(cacheKey, {
          data: trendData,
          timestamp: Date.now(),
        });
      }
    }
  }, [trendData, selectedTimeRange, isLoadingTrend, getCacheKey]);

  // Check cache when time range changes (for instant switching between 24h, 7d, 30d)
  useEffect(() => {
    // Always clear cached data first when time range changes to prevent showing wrong data
    setCachedTrendData(null);

    // Only check cache for non-Custom ranges
    if (selectedTimeRange === "Custom") {
      return;
    }

    const cacheKey = getCacheKey();
    if (!cacheKey) {
      return;
    }

    // Check cache immediately for instant switching
    // This allows switching between 24h, 7d, 30d to be instant if data is cached
    const cached = trendDataCacheRef.current.get(cacheKey);
    if (cached && cached.data && cached.data.length > 0) {
      // Verify the cached data is for the current time range by checking the cache key
      const currentCacheKey = getCacheKey();
      if (currentCacheKey === cacheKey) {
        // Use cached data immediately for this time range
        // Downtime overlay will also be cached and retrieved instantly via processedDowntimeEvents
        setCachedTrendData(cached.data);
      }
    }
    // If no cache exists, the hook will fetch fresh data and it will be displayed via trendData
    // The downtime overlay will be computed and cached when the data loads
  }, [selectedTimeRange, getCacheKey]);

  // Use cached data if available, otherwise use prop data
  // Only use cached data if it's for the current time range
  const displayTrendData = useMemo(() => {
    // Always prefer fresh trendData from the hook (which is fetched for the current time range)
    // This ensures we show the correct data when switching time ranges
    if (trendData && trendData.length > 0) {
      return trendData;
    }

    // Only use cached data if it's verified to be for the current time range
    if (cachedTrendData && cachedTrendData.length > 0) {
      const cacheKey = getCacheKey();
      if (cacheKey) {
        const cached = trendDataCacheRef.current.get(cacheKey);
        // Only use cached data if it matches the current cache key (same time range + params)
        if (cached && cached.data === cachedTrendData) {
          return cachedTrendData;
        }
      }
      // If cached data doesn't match current time range, clear it
      setCachedTrendData(null);
    }

    // Return empty array if no data available
    return [];
  }, [cachedTrendData, trendData, getCacheKey]);

  // Track when time range changes to trigger x-axis reset
  useEffect(() => {
    if (previousTimeRangeRef.current !== selectedTimeRange) {
      previousTimeRangeRef.current = selectedTimeRange;
      needsXAxisResetRef.current = true;
      setIsZoomed(false);
    }
  }, [selectedTimeRange, setIsZoomed]);

  // Update chart when displayTrendData changes to ensure it reflects the fetched data
  useEffect(() => {
    if (!displayTrendData || displayTrendData.length === 0) {
      return;
    }

    // Use a small delay to ensure chart instance is ready
    const timer = setTimeout(() => {
      const chart = getChartInstance();
      if (!chart || !chart.series) {
        return;
      }

      // Force chart to update with new series data
      const { series } = createChartSeries(displayTrendData);

      // Update existing series
      chart.series.forEach((s, index) => {
        if (series[index]) {
          s.update(
            {
              data: series[index].data,
              name: series[index].name,
              color: series[index].color,
            },
            false
          ); // false = don't redraw yet
        }
      });

      // Add new series if there are more than existing
      if (series.length > chart.series.length) {
        for (let i = chart.series.length; i < series.length; i++) {
          chart.addSeries(series[i], false); // false = don't redraw yet
        }
      }

      // Remove extra series if there are fewer
      while (chart.series.length > series.length) {
        chart.series[chart.series.length - 1].remove(false); // false = don't redraw yet
      }

      // Redraw chart once after all updates
      chart.redraw();

      // Reset x-axis extremes if needed
      if (needsXAxisResetRef.current && chart.xAxis?.[0]) {
        const xAxis = chart.xAxis[0];
        if (
          xAxis.dataMin !== null
          && xAxis.dataMax !== null
          && !isNaN(xAxis.dataMin)
          && !isNaN(xAxis.dataMax)
        ) {
          xAxis.setExtremes(null, null, true);
          needsXAxisResetRef.current = false;
          setIsZoomed(false);
        }
      }
    }, 100); // Small delay to ensure chart is ready

    return () => clearTimeout(timer);
  }, [displayTrendData, getChartInstance, createChartSeries, setIsZoomed]);

  // Helper: Format duration in hours (e.g., "0.07 h", "2.00 h")
  const formatDurationInHours = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return "0.00 h";
    const hours = minutes / 60;
    return `${hours.toFixed(2)} h`;
  }, []);

  // Helper: Format date for downtime annotation (e.g., "Jan 10, 25 11:25 PM")
  // Process downtime events for the current asset and time range (with caching)
  const processedDowntimeEvents = useMemo(() => {
    if (
      !downtimeEvents
      || !Array.isArray(downtimeEvents)
      || assetId == null
      || assetId === ""
      || !displayTrendData
      || displayTrendData.length === 0
    ) {
      return [];
    }

    // Calculate chart time range from trendData (used for overlap filter only).
    // Do not use a separate ref-Map cache here: useMemo already keys off `displayTrendData`,
    // but trend series can change time extent or point count while downtime row/event counts
    // stay the same, which previously produced stale empty overlay results on Trends.
    let chartMinTime = Infinity;
    let chartMaxTime = -Infinity;

    displayTrendData.forEach((item) => {
      if (item.data && item.data.length > 0) {
        item.data.forEach((point) => {
          const timestamp = new Date(point.timestamp).getTime();
          if (timestamp < chartMinTime) chartMinTime = timestamp;
          if (timestamp > chartMaxTime) chartMaxTime = timestamp;
        });
      }
    });

    if (chartMinTime === Infinity || chartMaxTime === -Infinity) {
      return [];
    }

    const assetDowntime = downtimeEvents.find((dt) =>
      sameDowntimeAssetPk(dt.assetId, assetId)
    );

    if (
      !assetDowntime
      || !assetDowntime.events
      || assetDowntime.events.length === 0
    ) {
      return [];
    }

    // Filter events that overlap with chart time range
    const overlappingEvents = assetDowntime.events.filter((event) => {
      const eventStart = new Date(event.dateTime).getTime();
      const eventEnd = event.dateTimeEnd
        ? new Date(event.dateTimeEnd).getTime()
        : Date.now(); // For active events, use current time

      // Check if event overlaps with chart range
      return eventEnd >= chartMinTime && eventStart <= chartMaxTime;
    });

    // Process events for chart display
    const processed = overlappingEvents.map((event) => {
      const eventStart = new Date(event.dateTime).getTime();
      const eventEnd = event.dateTimeEnd
        ? new Date(event.dateTimeEnd).getTime()
        : Date.now();
      const durationMinutes =
        event.duration || (eventEnd - eventStart) / (1000 * 60);
      const durationHours = formatDurationInHours(durationMinutes);

      // Get event label (description or "Unknown")
      const label = event.relatedAlarm || "Unknown";
      // Truncate long labels
      const displayLabel =
        label.length > MAX_LABEL_LENGTH
          ? label.substring(0, MAX_LABEL_LENGTH - 3) + "..."
          : label;

      return {
        startTime: eventStart,
        endTime: eventEnd,
        label: displayLabel,
        fullLabel: label,
        startDate: formatChartTooltipDateTime(event.dateTime),
        endDate: formatChartTooltipDateTime(event.dateTimeEnd || event.dateTime),
        duration: durationHours,
        durationMinutes,
      };
    });

    return processed;
  }, [
    downtimeEvents,
    assetId,
    displayTrendData,
    selectedTimeRange,
    customStartDate,
    customEndDate,
    formatDurationInHours,
    formatChartTooltipDateTime,
  ]);

  // Handle mouse move over chart to detect hover over downtime periods
  // Throttled for performance - only updates every 50ms
  const handleChartMouseMove = useCallback(
    (e, chart) => {
      // Throttle: only process if enough time has passed since last call
      if (mouseMoveThrottleRef.current) {
        return;
      }

      mouseMoveThrottleRef.current = setTimeout(() => {
        mouseMoveThrottleRef.current = null;
      }, MOUSE_THROTTLE_MS);

      if (
        !chart
        || !chart.xAxis
        || !chart.xAxis[0]
        || !processedDowntimeEvents.length
      ) {
        return;
      }

      const xAxis = chart.xAxis[0];
      const plotLeft = chart.plotLeft;
      const plotTop = chart.plotTop;
      const plotHeight = chart.plotHeight;
      const plotWidth = chart.plotWidth;

      // Get mouse position relative to chart
      const chartRect = chart.container.getBoundingClientRect();
      const mouseX = e.clientX - chartRect.left;
      const mouseY = e.clientY - chartRect.top;

      // Check if mouse is over the plot area
      if (
        mouseX < plotLeft
        || mouseX > plotLeft + plotWidth
        || mouseY < plotTop
        || mouseY > plotTop + plotHeight
      ) {
        if (lastHoveredIndexRef.current !== null) {
          lastHoveredIndexRef.current = null;
          setHoveredAnnotationIndex(null);
        }
        return;
      }

      // Convert mouse X position to timestamp
      const timestamp = xAxis.toValue(mouseX);

      // Find which downtime event the mouse is over
      // Use find for early exit instead of forEach
      let hoveredIndex = null;
      for (let i = 0; i < processedDowntimeEvents.length; i++) {
        const event = processedDowntimeEvents[i];
        if (timestamp >= event.startTime && timestamp <= event.endTime) {
          hoveredIndex = i;
          break; // Early exit once found
        }
      }

      // Only update state if the hovered index actually changed
      if (lastHoveredIndexRef.current !== hoveredIndex) {
        lastHoveredIndexRef.current = hoveredIndex;
        setHoveredAnnotationIndex(hoveredIndex);
      }
    },
    [processedDowntimeEvents]
  );

  // Helper: Create mouse event handlers
  const createMouseHandlers = useCallback(
    (chart) => {
      const mouseMoveHandler = (e) => {
        handleChartMouseMove(e, chart);
      };
      const mouseLeaveHandler = () => {
        if (mouseMoveThrottleRef.current) {
          clearTimeout(mouseMoveThrottleRef.current);
          mouseMoveThrottleRef.current = null;
        }
        lastHoveredIndexRef.current = null;
        setHoveredAnnotationIndex(null);
      };
      return { mouseMoveHandler, mouseLeaveHandler };
    },
    [handleChartMouseMove]
  );

  // Function to update annotation positions based on chart x-axis
  // Uses requestAnimationFrame for smooth, batched DOM updates
  const updateAnnotationPositions = useCallback(() => {
    // Skip if overlay is hidden
    if (!showDowntimeOverlay) return;

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const chart = chartInstanceRef.current;
      if (
        !chart
        || !chart.xAxis
        || !chart.xAxis[0]
        || !annotationContainerRef.current
      ) {
        return;
      }

      const xAxis = chart.xAxis[0];
      const plotLeft = chart.plotLeft;
      const plotTop = chart.plotTop;
      const plotHeight = chart.plotHeight;
      const extremes = xAxis.getExtremes();
      // Place chips below the datetime axis (same band as chart.marginBottom), not on top of ticks.
      const plotBottom = plotTop + plotHeight;
      const numYAxes = chart.yAxis?.length ?? 1;
      const axisBottomMarginPx =
        numYAxes > 1 ? TREND_X_AXIS_MARGIN_MULTI : TREND_X_AXIS_MARGIN_SINGLE;
      const labelTop = plotBottom + axisBottomMarginPx + 14;

      // Batch DOM updates by collecting all changes first
      const updates = [];
      for (let index = 0; index < processedDowntimeEvents.length; index++) {
        const event = processedDowntimeEvents[index];
        const annotationElement = annotationContainerRef.current?.querySelector(
          `[data-downtime-event="${index}"]`
        );
        if (!annotationElement) continue;

        // Check if the event is within the visible range first (cheaper check)
        if (event.endTime < extremes.min || event.startTime > extremes.max) {
          updates.push({ element: annotationElement, visible: false });
          continue;
        }

        // Get pixel position for the event start time
        const pixelPosition = xAxis.toPixels(event.startTime);
        if (pixelPosition === null || pixelPosition === undefined) {
          updates.push({ element: annotationElement, visible: false });
          continue;
        }

        // Position relative to the chart container, offset to the right of the start line
        const leftPosition = pixelPosition - plotLeft;
        updates.push({
          element: annotationElement,
          visible: true,
          left: `${leftPosition + ANNOTATION_OFFSET_PX}px`,
          top: `${labelTop}px`,
        });
      }

      // Apply all DOM updates in a single batch
      updates.forEach(({ element, visible, left, top }) => {
        if (visible) {
          element.style.display = "block";
          element.style.left = left;
          element.style.top = top;
          element.style.transform = "";
        } else {
          element.style.display = "none";
          element.style.transform = "";
        }
      });
    });
  }, [processedDowntimeEvents, showDowntimeOverlay]);

  // Update annotation positions when chart redraws or data changes
  useEffect(() => {
    if (
      displayTrendData
      && displayTrendData.length > 0
      && processedDowntimeEvents.length > 0
    ) {
      // Use requestAnimationFrame for smoother updates
      const rafId = requestAnimationFrame(() => {
        updateAnnotationPositions();
      });
      return () => {
        cancelAnimationFrame(rafId);
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
        }
      };
    }
  }, [
    trendData,
    displayTrendData,
    processedDowntimeEvents,
    updateAnnotationPositions,
  ]);

  // Reset zoom state - update annotations after reset
  useEffect(() => {
    if (displayTrendData && displayTrendData.length > 0) {
      const rafId = requestAnimationFrame(() => {
        if (updateAnnotationPositions) {
          updateAnnotationPositions();
        }
      });
      return () => {
        cancelAnimationFrame(rafId);
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
        }
      };
    }
  }, [isZoomed, updateAnnotationPositions, trendData, displayTrendData]);

  // Clear hover state when overlay is toggled off
  useEffect(() => {
    if (!showDowntimeOverlay) {
      setHoveredAnnotationIndex(null);
      // Clear throttle timeout
      if (mouseMoveThrottleRef.current) {
        clearTimeout(mouseMoveThrottleRef.current);
        mouseMoveThrottleRef.current = null;
      }
      lastHoveredIndexRef.current = null;
    }
  }, [showDowntimeOverlay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup throttle timeout
      if (mouseMoveThrottleRef.current) {
        clearTimeout(mouseMoveThrottleRef.current);
        mouseMoveThrottleRef.current = null;
      }
      // Cleanup animation frame
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const scheduleReflow = () => {
      requestAnimationFrame(() => {
        const chart = getChartInstance();
        if (chart && typeof chart.reflow === "function") {
          chart.reflow();
        }
      });
    };

    const ro = new ResizeObserver(scheduleReflow);
    ro.observe(el);
    scheduleReflow();
    return () => ro.disconnect();
  }, [getChartInstance, displayTrendData]);

  const chartOptions = useMemo(() => {
    if (!displayTrendData || displayTrendData.length === 0) {
      return {
        chart: { type: "line", backgroundColor: "transparent" },
        series: [],
      };
    }

    const { series, unitAxisAssignments, numAxes, hasMultipleAxes } =
      createChartSeries(displayTrendData);

    // Create plotBands for downtime events (red background fill from start to end)
    // Only create if overlay is visible
    const plotBands = showDowntimeOverlay
      ? processedDowntimeEvents.map((event) => ({
          from: event.startTime,
          to: event.endTime,
          color: DOWNTIME_COLORS.background,
          zIndex: 0,
        }))
      : [];

    // Create plotLines for downtime events (vertical red dotted lines at start and end)
    // Only create if overlay is visible
    const plotLines = showDowntimeOverlay
      ? processedDowntimeEvents.flatMap((event) => [
          {
            value: event.startTime,
            color: DOWNTIME_COLORS.line,
            width: 1,
            dashStyle: "Dot",
            zIndex: 10,
          },
          {
            value: event.endTime,
            color: DOWNTIME_COLORS.line,
            width: 1,
            dashStyle: "Dot",
            zIndex: 10,
          },
        ])
      : [];

    // Keep the x-axis visible, and reserve a row below it for downtime HTML chips so they
    // do not cover datetime tick labels (they were positioned with a fixed gap from plotBottom).
    const axisBottomMarginPx = hasMultipleAxes
      ? TREND_X_AXIS_MARGIN_MULTI
      : TREND_X_AXIS_MARGIN_SINGLE;
    const downtimeHtmlRowPx =
      showDowntimeOverlay && processedDowntimeEvents.length > 0
        ? DOWNTIME_HTML_LABEL_SLOT_PX
        : 0;
    const chartBottomMargin = axisBottomMarginPx + downtimeHtmlRowPx;

    return {
      chart: {
        type: "line",
        backgroundColor: "transparent",
        zoomType: "x",
        marginBottom: chartBottomMargin,
        panning: {
          enabled: true,
          type: "x",
        },
        style: {
          zIndex: 1, // Lower z-index for chart background
        },
        events: {
          load: function () {
            chartInstanceRef.current = this;

            // Reset x-axis extremes if needed (for initial load with new time range)
            const xAxis = this.xAxis[0];
            if (
              needsXAxisResetRef.current
              && xAxis
              && xAxis.dataMin !== null
              && xAxis.dataMax !== null
            ) {
              xAxis.setExtremes(null, null, false);
              needsXAxisResetRef.current = false;
            }

            // Update annotation positions after chart loads
            updateAnnotationPositions();

            // Add mousemove and mouseleave handlers to detect hover over downtime periods
            // Only if downtime overlay is visible
            if (showDowntimeOverlay) {
              const chart = this;
              const container = chart.container;
              if (container) {
                const { mouseMoveHandler, mouseLeaveHandler } =
                  createMouseHandlers(chart);
                container.addEventListener("mousemove", mouseMoveHandler);
                container.addEventListener("mouseleave", mouseLeaveHandler);
                // Store handlers for cleanup
                chart._downtimeMouseMoveHandler = mouseMoveHandler;
                chart._downtimeMouseLeaveHandler = mouseLeaveHandler;
              }
            }
          },
          selection: function (e) {
            if (e.xAxis && e.xAxis.length > 0) {
              setIsZoomed(true);
            }
          },
          redraw: function () {
            const xAxis = this.xAxis[0];
            const extremes = xAxis.getExtremes();
            const dataMin = xAxis.dataMin;
            const dataMax = xAxis.dataMax;

            // Reset x-axis extremes if time range changed and we have valid data
            if (
              needsXAxisResetRef.current
              && dataMin !== null
              && dataMax !== null
              && !isNaN(dataMin)
              && !isNaN(dataMax)
            ) {
              // Reset to show full data range
              // Use setTimeout to ensure this happens after the current redraw cycle
              setTimeout(() => {
                if (needsXAxisResetRef.current) {
                  xAxis.setExtremes(null, null, true);
                  needsXAxisResetRef.current = false;
                }
              }, 0);
            }

            const isCurrentlyZoomed =
              extremes.min !== dataMin || extremes.max !== dataMax;
            setIsZoomed(isCurrentlyZoomed);

            // Update annotation positions after redraw
            updateAnnotationPositions();

            // Re-attach mousemove and mouseleave handlers if needed
            // Only if downtime overlay is visible
            const chart = this;
            const container = chart.container;
            if (showDowntimeOverlay) {
              if (container && !chart._downtimeMouseMoveHandler) {
                const { mouseMoveHandler, mouseLeaveHandler } =
                  createMouseHandlers(chart);
                container.addEventListener("mousemove", mouseMoveHandler);
                container.addEventListener("mouseleave", mouseLeaveHandler);
                chart._downtimeMouseMoveHandler = mouseMoveHandler;
                chart._downtimeMouseLeaveHandler = mouseLeaveHandler;
              }
            } else {
              // Remove handlers if overlay is hidden
              if (container && chart._downtimeMouseMoveHandler) {
                container.removeEventListener(
                  "mousemove",
                  chart._downtimeMouseMoveHandler
                );
                container.removeEventListener(
                  "mouseleave",
                  chart._downtimeMouseLeaveHandler
                );
                chart._downtimeMouseMoveHandler = null;
                chart._downtimeMouseLeaveHandler = null;
              }
              // Clear hover state
              setHoveredAnnotationIndex(null);
            }
          },
        },
      },
      title: {
        text: null,
      },
      xAxis: {
        type: "datetime",
        title: {
          text: null,
        },
        ...baseAxisConfig,
        // Do not let Highcharts drop the axis when vertical space is tight
        showEmpty: true,
        labels: {
          ...baseAxisConfig.labels,
          reserveSpace: true,
        },
        plotBands,
        plotLines,
        events: {
          afterSetExtremes: function () {
            const extremes = this.getExtremes();
            const dataMin = this.dataMin;
            const dataMax = this.dataMax;
            const isCurrentlyZoomed =
              extremes.min !== dataMin || extremes.max !== dataMax;
            setIsZoomed(isCurrentlyZoomed);
          },
        },
      },
      yAxis: hasMultipleAxes
        ? // Multiple Y-axes: one per unique unit, alternating left/right
          unitAxisAssignments.map((assignment, index) => {
            const isRightSide = index % 2 === 1;
            // Calculate offset for multiple axes on the same side
            // Left side: indices 0, 2, 4, ... -> offsets 0, 60, 120, ...
            // Right side: indices 1, 3, 5, ... -> offsets 0, 60, 120, ...
            const sideIndex = Math.floor(index / 2);
            const offset = sideIndex * 60; // 60px offset per axis on same side

            return {
              title: {
                text: assignment.unit?.trim() || null,
                rotation: -90,
                align: "middle",
                style: {
                  fontSize: "12px",
                  color: "hsl(var(--muted-foreground))",
                },
              },
              labels: {
                style: {
                  fontSize: "12px",
                  color: "hsl(var(--muted-foreground))",
                },
              },
              opposite: isRightSide, // Position on right side for odd indices
              offset: offset, // Offset for multiple axes on same side
              ...baseAxisConfig,
            };
          })
        : // Single Y-axis (when all series use same unit)
          {
            title: {
              text:
                unitAxisAssignments.length > 0
                  ? unitAxisAssignments[0].unit?.trim() || null
                  : null,
              rotation: -90,
              align: "middle",
              style: {
                fontSize: "12px",
                color: "hsl(var(--muted-foreground))",
              },
            },
            labels: {
              style: {
                fontSize: "12px",
                color: "hsl(var(--muted-foreground))",
              },
            },
            ...baseAxisConfig,
          },
      tooltip: {
        ...getChartTooltipChrome(),
        borderRadius: 12,
        borderWidth: 1,
        shadow: {
          color: "rgba(0, 0, 0, 0.1)",
          offsetX: 0,
          offsetY: 4,
          opacity: 0.1,
          width: 8,
        },
        padding: 0,
        useHTML: true,
        shared: true,
        outside: true,
        formatter: formatMultiSeriesTooltip(displayTrendData),
        style: {
          fontFamily: "var(--font-sans)",
          color: "hsl(var(--foreground))",
          zIndex: 9999,
          pointerEvents: "none",
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          zIndex: 1, // Lower z-index for series lines
        },
      },
      series,
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }, [
    displayTrendData,
    selectedTimeRange, // Include time range to force chart recalculation
    createChartSeries,
    formatMultiSeriesTooltip,
    setIsZoomed,
    processedDowntimeEvents,
    updateAnnotationPositions,
    handleChartMouseMove,
    showDowntimeOverlay,
    createMouseHandlers,
    theme,
  ]);

  return (
    <div className='flex h-full w-full min-w-0 max-w-full flex-col gap-4'>
      {/* Date Grouping - Outside Container */}
      <div
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-y-2",
          selectedTimeRange === "Custom" ? "w-full" : "justify-end gap-x-2"
        )}>
        {selectedTimeRange === "Custom" ? (
          <CustomDateRangePicker
            startDate={customStartDate}
            endDate={customEndDate}
            onApply={applyCustomDateRange}
            onClear={() => {
              setSelectedTimeRange(previousTimeRange);
              setCustomStartDate("");
              setCustomEndDate("");
            }}
            disabled={isLoadingTrend}
          />
        ) : (
          <div className={cn(dateRangePillTrack, "justify-end")}>
            {timeRangeOptions.map((range) => (
              <Button
                key={range}
                variant='outline'
                size='sm'
                onClick={() => {
                  if (range === "Custom") {
                    setPreviousTimeRange(selectedTimeRange);
                  }
                  setSelectedTimeRange(range);
                }}
                className={cn(
                  "rounded-full whitespace-nowrap shadow-none font-medium",
                  selectedTimeRange === range
                    ? dateRangePillSelected
                    : dateRangePillIdle
                )}>
                {range}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Trend Analysis Card */}
      <Card className='flex flex-col h-full overflow-hidden bg-card border border-border shadow-sm rounded-lg'>
        <CardContent className='flex min-h-0 flex-1 flex-col overflow-hidden p-6'>
          {/* Header Section */}
          <div className='mb-4 flex shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <h2 className='text-lg font-semibold text-foreground'>
              Trend Analysis
            </h2>
            {/* Action Buttons Row */}
            <div className='flex items-center justify-end gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadTrendAnalysis(String(assetId), displayTrendData)
                }
                disabled={isLoadingTrend}
                className={cn(
                  steelChromeButton,
                  "rounded-full p-2 disabled:opacity-50"
                )}>
                <FileDown className='h-4 w-4' />
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setShowDowntimeOverlay(!showDowntimeOverlay)}
                className={cn(
                  "rounded-full p-2 shadow-none",
                  showDowntimeOverlay
                    ? steelChromeButton
                    : "border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                title={
                  showDowntimeOverlay
                    ? "Hide downtime overlay"
                    : "Show downtime overlay"
                }>
                {showDowntimeOverlay ? (
                  <Eye className='h-4 w-4' />
                ) : (
                  <EyeOff className='h-4 w-4' />
                )}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleZoomIn}
                disabled={!displayTrendData || displayTrendData.length === 0}
                className={cn(
                  steelChromeButton,
                  "rounded-full p-2 disabled:opacity-50"
                )}
                title='Zoom in'>
                <ZoomIn className='h-4 w-4' />
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleZoomOut}
                disabled={
                  !isZoomed
                  || !displayTrendData
                  || displayTrendData.length === 0
                }
                className={cn(
                  steelChromeButton,
                  "rounded-full p-2 disabled:opacity-50"
                )}
                title='Zoom out'>
                <ZoomOut className='h-4 w-4' />
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={fetchTrendData}
                disabled={isLoadingTrend}
                className={cn(
                  steelChromeButton,
                  "rounded-full p-2 disabled:opacity-50"
                )}>
                <RefreshCw
                  className={cn("h-4 w-4", isLoadingTrend && "animate-spin")}
                />
              </Button>
            </div>
          </div>

          {/* Scroll when legend + chart need more height than the panel (chart keeps a usable min size) */}
          <div className='flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain'>
          {/* Chart Display Area — overflow-hidden keeps plot from painting over the legend on resize */}
          <div
            ref={chartAreaRef}
            className='relative flex h-[clamp(360px,52vh,720px)] min-h-[360px] w-full shrink-0 flex-col overflow-hidden rounded border border-border bg-card p-4'>
            {isLoadingTrend ? (
              <div className='flex min-h-0 flex-1 items-center justify-center'>
                <div className='flex flex-col items-center gap-3'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
                  <p className='text-sm text-muted-foreground'>
                    Loading trend data...
                  </p>
                </div>
              </div>
            ) : trendError ? (
              <div className='flex min-h-0 flex-1 items-center justify-center'>
                <div className='text-center'>
                  <p className='text-red-600 mb-2'>{trendError}</p>
                </div>
              </div>
            ) : !displayTrendData || displayTrendData.length === 0 ? (
              <div className='flex min-h-0 flex-1 items-center justify-center'>
                <p className='text-muted-foreground'>
                  Select tags to view trend analysis
                </p>
              </div>
            ) : (
              <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
                <div
                  className='relative min-h-0 w-full flex-1 basis-0'
                  style={{ minHeight: 0 }}>
                  <HighchartsReact
                    key={`${selectedTimeRange}-${displayTrendData?.length || 0}-${Array.from(selectedParameters).sort().join("-")}`}
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                    allowChartUpdate={true}
                    updateArgs={[true, true, true]}
                    containerProps={{
                      className: "h-full w-full min-h-0",
                      style: { height: "100%", minHeight: 0 },
                    }}
                  />
                {/* Annotation boxes rendered outside Highcharts container */}
                {showDowntimeOverlay && processedDowntimeEvents.length > 0 && (
                  <div
                    ref={annotationContainerRef}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                      zIndex: 1000,
                    }}>
                    {processedDowntimeEvents.map((event, index) => {
                      const isHovered = hoveredAnnotationIndex === index;
                      return (
                        <div
                          key={`${event.startTime}-${event.endTime}-${index}`}
                          data-downtime-event={index}
                          onMouseEnter={() => {
                            lastHoveredIndexRef.current = index;
                            setHoveredAnnotationIndex(index);
                          }}
                          onMouseLeave={(e) => {
                            const next = e.relatedTarget;
                            if (
                              next instanceof Node
                              && annotationContainerRef.current?.contains(
                                next
                              )
                            ) {
                              return;
                            }
                            lastHoveredIndexRef.current = null;
                            setHoveredAnnotationIndex(null);
                          }}
                          style={{
                            position: "absolute",
                            backgroundColor: DOWNTIME_COLORS.annotation,
                            color: "white",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: 500,
                            whiteSpace: isHovered ? "normal" : "nowrap",
                            maxWidth: isHovered ? "min(320px, calc(100% - 16px))" : undefined,
                            boxShadow: isHovered
                              ? "0 4px 8px rgba(0,0,0,0.3)"
                              : "0 2px 4px rgba(0,0,0,0.1)",
                            transform: isHovered ? "scale(1.05)" : "scale(1)",
                            pointerEvents: "auto",
                            zIndex: isHovered ? 10000 : 10 + index,
                            transition: "all 0.2s ease-in-out",
                          }}>
                          <div style={{ fontWeight: 600, marginBottom: "2px" }}>
                            {isHovered && event.fullLabel
                              ? event.fullLabel
                              : event.label}
                          </div>
                          <div style={{ fontSize: "10px", opacity: 0.95 }}>
                            Start: {event.startDate}
                            <br />
                            End: {event.endDate}
                            <br />
                            Downtime: {event.duration}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            )}
          </div>

          {/* Legend Section */}
          {displayTrendData && displayTrendData.length > 0 && (
            <div className='mt-4 shrink-0 border-t border-border pt-4'>
              <div className='max-h-40 overflow-y-auto overflow-x-hidden'>
                <div className='flex items-center justify-center gap-4 flex-wrap'>
                  {displayTrendData.map((item, index) => {
                    const color = SERIES_COLORS[index % SERIES_COLORS.length];
                    const unit = item.param?.unit || "";
                    return (
                      <span
                        key={`${item.param.category}-${item.param.parameter}`}
                        className='text-sm text-foreground flex items-center gap-1'>
                        <span
                          className='inline-block w-3 h-3 rounded'
                          style={{ backgroundColor: color }}></span>
                        → {item.param.parameter}
                        {unit && (
                          <span className='text-muted-foreground'>
                            ({unit})
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
